import { Dayjs } from 'dayjs';
import { GetCampaignResponse } from './campaignCreator';

export interface DropdownOption {
  displayName: string;
  displayNameFiltered?: string;
  enabled: boolean;
}
export interface DropdownOptions {
  [key: string]: DropdownOption;
}

export type RelativeDate = 'anytime' | 'last_24_hours' | 'last_week' | 'last_month' | 'custom';
export type GroupBy = 'none' | 'last_heard';
export const AUDIENCE_ENTITY_TYPE = {
  touchstone: 'touchstone',
  adsb: 'adsb',
  ais: 'ais'
} as const;

export type AudienceEntityTypeFilter = (typeof AUDIENCE_ENTITY_TYPE)[keyof typeof AUDIENCE_ENTITY_TYPE];

export const AUDIENCE_ENTITY_TYPE_OPTIONS: AudienceEntityTypeFilter[] = ['touchstone', 'adsb', 'ais'];

export const normalizeAdsbCallsign = (callsign?: string | null): string => callsign || 'UNK';

export const getAdsbAudienceId = (icao?: string | null, callsign?: string | null): string => {
  return `${icao || 'UNK'} - ${normalizeAdsbCallsign(callsign)}`;
};

export const getAisAudienceId = (mmsi: string): string => `MMSI ${mmsi}`;

export const getAudienceEntityType = (entity: Audience): AudienceEntityTypeFilter => {
  return entity.source === AUDIENCE_ENTITY_TYPE.adsb || entity.source === AUDIENCE_ENTITY_TYPE.ais ? entity.source : AUDIENCE_ENTITY_TYPE.touchstone;
};

/** AdTech entities are the only ones whose ID is an advertiser ID the touchstone APIs understand. */
export const isAdTechEntity = (entity?: Audience): boolean => !entity || getAudienceEntityType(entity) === AUDIENCE_ENTITY_TYPE.touchstone;

const AUDIENCE_ENTITY_TYPE_LABELS: Record<AudienceEntityTypeFilter, string> = {
  touchstone: 'AdTech',
  adsb: 'ADS-B',
  ais: 'AIS'
};

export const getAudienceEntityTypeLabel = (entityType: AudienceEntityTypeFilter): string => {
  return AUDIENCE_ENTITY_TYPE_LABELS[entityType];
};

export const matchesAudienceEntityTypeFilter = (entity: Audience, entityTypes: AudienceEntityTypeFilter[]): boolean => {
  if (entityTypes.length === 0) return false;
  return entityTypes.includes(getAudienceEntityType(entity));
};

export const isAudienceEntityTypeEnabled = (entityTypes: AudienceEntityTypeFilter[], entityType: AudienceEntityTypeFilter): boolean => {
  return entityTypes.includes(entityType);
};

export type AudienceScoreMap = Record<
  string,
  {
    min: number;
    max: number;
    sum: number;
  }
>;

export interface Audience {
  advertiser_id: string;
  /** Polygons visited all time. Counted service-side; see getAudiencePolygonCount. */
  n_polygons?: number;
  /**
   * The pre-count response shape. Kept only so this app can deploy ahead of the
   * service that stopped sending it; delete once that has shipped, along with
   * the fallback in getAudiencePolygonCount.
   */
  polygon_ids?: string[];
  // manually added at the service level
  n_within_sel: number;
  // from join ( ts_polyscores_summary )
  max_pred1_all_time?: string | null;
  avg_pred1_six_months?: string | null;
  source?: AudienceEntityTypeFilter;
  adsbIcao?: string;
  adsbCallsign?: string;
  aisMmsi?: string;
  aisVesselName?: string;
}

/**
 * How many polygons the entity has visited all time.
 *
 * The service used to send the ids and the app only ever counted them, on
 * responses running to 10,000 rows, so it now sends the count. The fallback is
 * the one place that tolerates an older service.
 */
export const getAudiencePolygonCount = (entity: Audience): number => entity.n_polygons ?? entity.polygon_ids?.length ?? 0;

export const getAudienceId = (entity: Audience): string => {
  if (entity.source === AUDIENCE_ENTITY_TYPE.adsb) {
    return getAdsbAudienceId(entity.adsbIcao, entity.adsbCallsign);
  }

  if (entity.source === AUDIENCE_ENTITY_TYPE.ais && entity.aisMmsi) {
    return getAisAudienceId(entity.aisMmsi);
  }

  return entity.advertiser_id;
};

export const getAudienceLabel = (entity: Audience): string => {
  if (entity.source === AUDIENCE_ENTITY_TYPE.ais && entity.aisVesselName) {
    return entity.aisVesselName;
  }

  return getAudienceId(entity);
};

export interface AudienceListPolygonInfo {
  name: string;
  polygon_id: string;
  entity_count: number;
  selected: boolean;
  collapsed: boolean;
}

export interface AvailableAudienceByPolygon {
  data?: Audience[];
  isActive: boolean;
  hideEmpty?: boolean;
}

export interface AudienceGroupLastHeard {
  last_heard: number;
  advertisers: string[];
  num_polygons: string;
}

export interface AudienceRequest {
  polygon_ids?: number[] | string[];
  audience_ids?: string[];
  min_polygon_interaction?: number;
  min_score?: number;
  sort: string;
  order?: 'asc' | 'desc';
  campaign?: null | GetCampaignResponse;
}

export interface AudienceRelationsRequest {
  entities: string[];
  campaign?: GetCampaignResponse;
}

export interface AudienceGroupRequest extends AudienceRequest {
  group_by?: GroupBy;
}

export type AudienceRenderMode = 'infinite' | 'grouped_by_last_heard' | 'grouped_by_polygon';

export interface FilterSource {
  [key: string]: {
    displayName: string;
    logo: string;
    default: boolean;
  };
}

export interface AudienceDetailPanelDateRange {
  minDate: Dayjs;
  maxDate: Dayjs;
  // epoch:
  minEpoch: number;
  maxEpoch: number;
  // human friendly:
  minDateStr: string;
  maxDateStr: string;

  singleDay: boolean;
}

export enum AudienceDetailPanelChartType {
  dwell = 'Dwell',
  activity = 'Activity',
  scores = 'Touchstone Scores'
}

export interface AuddienceDetailPanelChartOptions {
  label: AudienceDetailPanelChartType;
  color: string;
}

import { BaseNote } from './common';

export type EntityNote = BaseNote;
